import { FormEvent, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createDistrict, getState, listDistricts } from "../../lib/api";
import { currentClaims } from "../../lib/auth";
import { InviteForm } from "../../components/admin/InviteForm";
import { EnterpriseBanner } from "../../components/admin/EnterpriseBanner";
import {
  Card,
  Crumbs,
  ErrorNote,
  Loading,
  PageTitle,
} from "../../components/admin/ui";

/** State detail: its districts, an add-district form, and per-district
 * "invite district head". Reachable by super, enterprise_admin, and
 * the state_head for this state. */
export function AdminState() {
  const { id } = useParams();
  const stateId = Number(id);
  const qc = useQueryClient();

  const stateQ = useQuery({
    queryKey: ["state", stateId],
    queryFn: () => getState(stateId),
  });
  const distQ = useQuery({
    queryKey: ["districts", stateId],
    queryFn: () => listDistricts({ state_id: stateId }),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["districts", stateId] });

  const state = stateQ.data?.state;
  // Only super + enterprise_admin can open the enterprise admin page;
  // a state_head's scope can't read it, so show that crumb as plain
  // text for them rather than a link that would bounce to "/".
  const role = currentClaims()?.role;
  const canOpenEnterprise =
    role === "agrilens_super" || role === "enterprise_admin";

  return (
    <div className="space-y-6">
      <Crumbs
        items={[
          ...(canOpenEnterprise
            ? [
                { label: "Enterprises", to: "/admin/enterprises" },
                {
                  label: "Enterprise",
                  to: state
                    ? `/admin/enterprise/${state.enterprise_id}`
                    : undefined,
                },
              ]
            : []),
          { label: state?.name ?? `State #${stateId}` },
        ]}
      />
      <PageTitle
        eyebrow="State"
        title={state?.name ?? `State #${stateId}`}
        subtitle="Districts in this state. Add districts and invite their heads."
      />

      <EnterpriseBanner />

      {state && (
        <Link
          to={`/state/${stateId}`}
          className="inline-flex items-center gap-1 text-sm text-agrilens-green hover:underline"
        >
          View reports &amp; rollups for this state →
        </Link>
      )}

      {stateQ.error && <ErrorNote error={stateQ.error} />}

      <AddDistrictForm stateId={stateId} onCreated={invalidate} />

      {distQ.isLoading && <Loading />}
      {distQ.error && <ErrorNote error={distQ.error} />}
      {distQ.data && (
        <div className="space-y-3">
          {distQ.data.districts.length === 0 && (
            <div className="text-sm text-gray-500">
              No districts yet — add one above.
            </div>
          )}
          {distQ.data.districts.map((d) => (
            <div key={d.id} className="bg-white rounded shadow p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Link
                  to={`/admin/district/${d.id}`}
                  className="text-lg font-semibold text-agrilens-green-dark hover:underline"
                >
                  {d.name}
                </Link>
                <Link
                  to={`/admin/district/${d.id}`}
                  className="text-sm text-agrilens-green hover:underline"
                >
                  Manage godowns →
                </Link>
              </div>
              <InviteForm
                role="district_head"
                roleLabel="district head"
                scopeType="district"
                scopeId={d.id}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddDistrictForm({
  stateId,
  onCreated,
}: {
  stateId: number;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");

  const mut = useMutation({
    mutationFn: () => createDistrict({ state_id: stateId, name: name.trim() }),
    onSuccess: () => {
      setName("");
      onCreated();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || mut.isPending) return;
    mut.mutate();
  };

  return (
    <Card title="Add district">
      <form onSubmit={onSubmit} className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-500 mb-1">
            District name
          </label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Saharsa"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={mut.isPending}
          className="bg-agrilens-green text-white text-sm font-medium px-4 py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
        >
          {mut.isPending ? "Adding…" : "Add district"}
        </button>
      </form>
      {mut.isError && (
        <div className="mt-2">
          <ErrorNote error={mut.error} />
        </div>
      )}
    </Card>
  );
}
